'use client';

import { fetcher, onSwrGlobalError, swrOnErrorRetry } from '@insta-club/api';
import { LoadingLayer } from '@insta-club/components';
import { useSessionStore } from '@insta-club/state';
import { Button } from '@insta-club/ui';
import { cn } from '@insta-club/utilities';
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { SWRConfig } from 'swr';

interface IProps {
    components: React.ReactNode;
}

function ClientPage({ components }: IProps) {
    const sessionStatus = useSessionStore((state) => state.status);

    const pathName = usePathname();
    const bottomPaddingPaths = [
        '/dashboard',
        '/analysis',
        '/explore',
        '/academy',
        '/profile',
        '/support',
        '/support/tickets',
        '/courses',
        '/subscription',
        '/videos',
        '/profile/favorite',
        '/privacy',
        '/terms',
        '/faq',
        '/wallet',
        '/wallet/deposit',
        '/tour',
        '/profile/history/analysis'
    ];
    const isIncludedBottomPaddingPaths = bottomPaddingPaths.includes(pathName);

    const [isOffline, setIsOffline] = useState(false);
    const [showLoader, setShowLoader] = useState(true);

    useEffect(() => {
        window.addEventListener('online', () => setIsOffline(false));
        window.addEventListener('offline', () => setIsOffline(true));
    }, []);

    useEffect(() => {
        if (sessionStatus !== 'loading') {
            const timeout = setTimeout(() => setShowLoader(false), 300);
            return () => clearTimeout(timeout);
        } else {
            setShowLoader(true);
        }
    }, [sessionStatus]);

    return (
        <>
            {isOffline && (
                <div className='fixed top-0 z-[999999999999999] flex h-full w-full max-w-[var(--container-max)] flex-col items-center justify-center bg-white'>
                    <div className='mx-auto flex max-w-screen-sm flex-col items-center justify-center space-y-8 text-center'>
                        <svg
                            width='233'
                            height='117'
                            viewBox='0 0 233 117'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                d='M209.242 98.7461C214.392 98.4445 216.949 100.686 218.148 105.477C223.807 106.319 225.73 109.146 225.545 114.654C225.033 115.256 223.764 116.627 222.944 116.699C217.931 117.14 212.648 116.895 207.592 116.824C203.136 116.788 198.679 116.785 194.222 116.815C189.772 114.185 191.602 109.164 195.437 107.003C198.006 105.556 200.237 105.792 202.946 106.584C203.283 102.126 205.151 100.163 209.242 98.7461Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M49.2789 68.8013C55.2135 68.1205 57.5634 71.1899 59.7641 76.0057C63.239 75.6024 64.9536 75.4901 66.67 79.0042C65.5293 84.8032 63.3982 84.1085 58.2306 84.0878L50.3827 84.0472C46.2133 84.0726 42.0847 84.0987 37.8883 84.0624C34.6972 84.0345 32.7984 82.3216 32.9485 79.0017C33.1326 74.9313 39.1433 75.8195 41.8996 76.0986C42.6877 71.9831 45.0567 69.4749 49.2789 68.8013Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M180.667 0.102355C186.151 -0.367355 188.598 2.00611 190.415 6.85263C192.136 6.67314 194.693 6.52744 195.722 7.92787C199.302 12.8015 193.283 14.2429 189.666 14.2028C186.793 14.1711 184.576 14.2007 182.131 14.1605C177.997 14.1293 173.963 14.3468 169.782 14.0659C166.113 13.8193 164.061 9.83045 167.199 7.35266C168.899 6.01064 171.637 6.52068 173.665 6.88768C174.776 3.41495 176.951 0.767137 180.667 0.102355Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M72.2383 18.4209C74.1607 19.2602 74.1582 19.4538 76.2635 18.606C75.3208 20.433 75.0776 20.7102 76.2052 22.5213L76.3272 22.6307L76.171 22.7186L74.3389 21.9159C73.7388 22.0155 73.0538 22.1854 72.452 22.3197C72.729 20.6335 72.7506 20.0667 72.2383 18.4209Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M228.731 82.3418C229.538 82.8743 229.871 83.0226 230.769 83.4027C231.499 83.2126 232.295 82.8972 233.01 82.6349C232.489 83.5344 232.325 84.0987 232.266 85.1182C232.413 85.6896 232.568 85.8264 232.347 86.2293C231.828 86.3978 230.134 86.1909 229.205 86.2183C229.65 84.3157 229.54 84.1291 228.731 82.3418Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M193.61 33.1888C194.402 33.4311 195.135 33.9396 195.845 34.3788C196.608 33.9993 197.298 33.5909 198.033 33.1631L197.039 35.2696C197.207 35.8927 197.52 36.6668 197.751 37.2872L197.902 37.5005L197.698 37.4785L197.698 37.4667C197.499 37.3577 197.296 37.2559 197.091 37.1605C195.847 36.5937 195.241 36.8002 194.07 37.2466C194.643 35.2747 194.808 34.9429 193.61 33.1888Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M0.112413 35.1367C1.4557 35.7642 2.12648 35.6251 3.48422 35.1893L3.4612 35.2987C3.17845 36.6977 3.01636 37.6297 3.77004 38.9554C3.916 39.2122 3.85569 39.0906 3.84484 39.4407C1.6036 37.1056 0.993721 38.9605 -0.00976562 38.7903C0.21356 37.0495 0.328643 36.8535 0.112413 35.1367Z'
                                fill='#EFEFF9'
                            />
                            <path
                                d='M27.3726 66.731C29.6696 68.1605 30.2456 67.0317 31.1076 67.5659C30.6371 68.9727 30.687 69.4262 31.3234 70.7346C29.6675 70.2899 29.0243 70.2249 27.4034 70.7034C27.6994 70.1345 27.9634 69.4326 28.2083 68.8286C27.9976 68.1686 27.6441 67.3826 27.3726 66.731Z'
                                fill='#EFEFF9'
                            />
                            <g clipPath='url(#clip0_19775_34592)'>
                                <path
                                    d='M134.796 90.5222C134.796 94.418 131.684 97.6055 127.88 97.6055C124.076 97.6055 120.963 94.418 120.963 90.5222C120.963 86.6264 124.076 83.4389 127.88 83.4389C131.684 83.4389 134.796 86.6264 134.796 90.5222ZM90.9793 48.2701C89.6997 49.4389 88.4547 50.7139 87.2788 51.9889C85.9647 53.4055 86.0684 55.6722 87.4518 56.9826C88.1088 57.6201 88.9734 57.9389 89.8034 57.9389C90.7372 57.9389 91.6709 57.5493 92.328 56.8055C93.3655 55.6722 94.4376 54.5743 95.5443 53.5472C96.9622 52.2368 97.1005 50.0055 95.8209 48.5535C94.5413 47.1014 92.3626 46.9597 90.9447 48.2701H90.9793ZM108.444 65.4118C107.061 66.5097 105.781 67.7139 104.605 69.0243C103.291 70.4764 103.395 72.7076 104.778 74.018C105.435 74.6555 106.3 74.9743 107.13 74.9743C108.063 74.9743 108.997 74.5847 109.654 73.841C110.588 72.8139 111.591 71.8576 112.663 71.0076C114.185 69.8034 114.461 67.5722 113.286 66.0493C112.11 64.491 109.931 64.2076 108.444 65.4118ZM133.378 66.3326C138.186 67.466 142.647 70.0868 146.036 73.8055C146.728 74.5493 147.627 74.9389 148.561 74.9389C149.391 74.9389 150.255 74.6201 150.912 73.9826C152.296 72.6368 152.399 70.4055 151.085 68.9889C144.722 61.9409 135.384 58.1514 126.116 58.6472C126.047 58.6472 125.943 58.6472 125.874 58.6472L111.418 43.8785C116.675 41.9305 122.208 40.9389 127.88 40.9389C141.298 40.9389 154.232 46.7118 163.397 56.8055C164.088 57.5493 164.988 57.9389 165.921 57.9389C166.751 57.9389 167.616 57.6201 168.273 56.9826C169.656 55.6368 169.76 53.4055 168.446 51.9889C158.002 40.4784 143.2 33.8555 127.88 33.8555C120.306 33.8555 112.974 35.3785 106.092 38.4243L92.2934 24.2576C90.9447 22.8764 88.7313 22.8764 87.3826 24.2576C86.0338 25.6389 86.0338 27.9055 87.3826 29.2868L163.466 107.203C164.158 107.912 165.022 108.231 165.921 108.231C166.821 108.231 167.685 107.876 168.377 107.203C169.726 105.822 169.726 103.591 168.377 102.21L133.413 66.368L133.378 66.3326Z'
                                    fill='#6262BE'
                                />
                            </g>
                            <defs>
                                <clipPath id='clip0_19775_34592'>
                                    <rect width='83' height='85' fill='white' transform='translate(86.3796 23.2305)' />
                                </clipPath>
                            </defs>
                        </svg>

                        <div className='space-y-3'>
                            <h1 className='text-xl font-bold text-gray-800'>عدم دسترسی به اینترنت !</h1>
                            <p className='text-[15px] font-normal text-gray-500'>
                                لطفاً اتصال اینترنت خود را بررسی کرده و پس از برقراری ارتباط، دوباره تلاش کنید.
                            </p>
                        </div>

                        <Button onClick={() => window.location.reload()} fullWidth>
                            دوباره تلاش کن
                        </Button>
                    </div>
                </div>
            )}

            <LoadingLayer show={showLoader} />

            <SWRConfig
                value={{
                    fetcher,
                    onErrorRetry: swrOnErrorRetry,
                    onError: onSwrGlobalError,
                    focusThrottleInterval: 30000
                }}
            >
                <div
                    className={cn(
                        'h-full overflow-y-scroll [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                        {
                            'pb-[100px]': isIncludedBottomPaddingPaths
                        }
                    )}
                >
                    {components}
                </div>
            </SWRConfig>
        </>
    );
}

export default ClientPage;

